import React from 'react';
import { Modal, Button, Typography } from '@crisellevaldez/speculo-ui';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  isLoading = false,
}) => {
  // Map the dialog variant onto a speculo-ui Button variant so the confirm
  // button uses the design-system colours (theme tokens, dark-mode aware)
  // rather than hard-coded hex values.
  const confirmButtonVariant =
    variant === 'danger' ? 'destructive' : variant === 'info' ? 'secondary' : 'primary';

  const getIcon = () => {
    const iconProps = { className: 'w-6 h-6' };
    switch (variant) {
      case 'danger':
        return <AlertCircle {...iconProps} className="w-6 h-6 text-red-600" />;
      case 'warning':
        return <AlertTriangle {...iconProps} className="w-6 h-6 text-yellow-500" />;
      case 'info':
        return <Info {...iconProps} className="w-6 h-6 text-blue-500" />;
      default:
        return <AlertCircle {...iconProps} className="w-6 h-6 text-red-600" />;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <Modal.Header>
        <div className="flex items-center gap-3">
          {getIcon()}
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">{title}</h3>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Typography variant="p" className="text-gray-600 dark:text-gray-400">
          {message}
        </Typography>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} size="sm" disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            size="sm"
            variant={confirmButtonVariant}
            isLoading={isLoading}
            disabled={isLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmationDialog;
